import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Close,
  ShieldOutlined,
  ShowChart,
  MedicalServices,
  Biotech,
  ContentCut,
  CameraAlt,
  DirectionsCar,
  Tune,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import api from '../../utils/api';
// This route renders OUTSIDE OfflineProvider, so useOffline() would silently
// default to online here — read the connectivity module directly instead,
// always paired with the OFFLINE_ENABLED flag.
import { OFFLINE_ENABLED } from '../../offline/entities';
import {
  isEffectivelyOnline,
  isTransportError,
  subscribeConnectivity,
  reportConnectivitySuccess,
  reportFromError,
} from '../../offline/connectivity';

const TOTAL_STEPS = 5;

interface OnboardingData {
  clinicName: string;
  website: string;
  servicesOffered: string[];
  teamSize: string;
  referralSource: string;
  currentSoftware: string;
  otherService: string;
  otherReferral: string;
  otherSoftware: string;
}

const SERVICE_OPTIONS = [
  { id: 'preventative_care', label: 'Preventative Care', icon: ShieldOutlined },
  { id: 'lameness_exams', label: 'Lameness Exams', icon: ShowChart },
  { id: 'dentistry', label: 'Dentistry', icon: MedicalServices },
  { id: 'reproduction', label: 'Reproduction', icon: Biotech },
  { id: 'surgery', label: 'Surgery', icon: ContentCut },
  { id: 'diagnostic_imaging', label: 'Diagnostic Imaging', icon: CameraAlt },
  { id: 'ambulatory', label: 'Ambulatory', icon: DirectionsCar },
  { id: 'chiropractic', label: 'Chiropractic', icon: Tune },
  { id: 'other', label: 'Other', icon: Add },
];

const TEAM_SIZE_OPTIONS = [
  { value: 'just_me', label: "It's just me" },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11+', label: '11+ people' },
];

const REFERRAL_OPTIONS = [
  { value: 'colleague', label: 'Recommended by a colleague' },
  { value: 'search_engine', label: 'Search engine (e.g. Google, Bing)' },
  { value: 'social_media', label: 'Social media' },
  { value: 'magazine_ad', label: 'Magazine ad' },
  { value: 'ratings_website', label: 'Ratings website (e.g. Capterra, Trustpilot)' },
  { value: 'other', label: 'Other' },
];

const SOFTWARE_OPTIONS = [
  'HVMS',
  'Vetera',
  'Daymark',
  'Stringsoft',
  'ezyVet',
  'Provet Cloud',
  'Cornerstone',
  'Hippo Manager',
  'Instinct',
  "I'm not using any software",
  'Other',
];

const Onboarding = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // 'blocked' = stopped pre-flight while offline; 'failed' = the request went
  // out but never reached the server (transport error) — retryable.
  const [completeError, setCompleteError] = useState<null | 'blocked' | 'failed'>(null);
  const [data, setData] = useState<OnboardingData>({
    clinicName: '',
    website: '',
    servicesOffered: [],
    teamSize: '',
    referralSource: '',
    currentSoftware: '',
    otherService: '',
    otherReferral: '',
    otherSoftware: '',
  });

  const [userData, setUserData] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    name?: string;
  }>({});

  useEffect(() => {
    const stored = localStorage.getItem('onboardingUserData');
    if (stored) {
      try {
        setUserData(JSON.parse(stored));
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  // Prefill from the account. Matters for a RE-RUN: an owner who was sent back
  // here (setup never submitted, e.g. they verified their email on another
  // device) would otherwise see a blank clinic name and think it was lost. The
  // localStorage handoff above only carries the person's own details, and it is
  // gone entirely by the time they return on a different day or device.
  useEffect(() => {
    let cancelled = false;
    api
      .get('/users/me')
      .then((res) => {
        if (cancelled) return;
        const me = res.data;
        setData((prev) => ({
          ...prev,
          clinicName: prev.clinicName || me?.clinic?.name || '',
          website: prev.website || me?.clinic?.website || '',
          teamSize: prev.teamSize || me?.clinic?.teamSize || '',
          referralSource: prev.referralSource || me?.clinic?.referralSource || '',
          currentSoftware: prev.currentSoftware || me?.clinic?.currentSoftware || '',
          servicesOffered: prev.servicesOffered.length
            ? prev.servicesOffered
            : me?.clinic?.servicesOffered ?? [],
        }));
        setUserData((prev) => ({
          firstName: prev.firstName || me?.firstName || '',
          lastName: prev.lastName || me?.lastName || '',
          phone: prev.phone || me?.phone || '',
          email: prev.email || me?.email || '',
          name: prev.name || me?.name || '',
        }));
      })
      .catch(() => {
        /* best-effort prefill — a fresh signup has nothing to prefill anyway */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear the offline block live on reconnect (browser events + the engine's
  // observed reachability) so the user can finish setup without a refresh.
  useEffect(() => {
    if (!OFFLINE_ENABLED) return undefined;
    const update = () => {
      if (isEffectivelyOnline()) {
        setCompleteError((prev) => (prev === 'blocked' ? null : prev));
      }
    };
    window.addEventListener('online', update);
    const unsubscribe = subscribeConnectivity(update);
    return () => {
      window.removeEventListener('online', update);
      unsubscribe();
    };
  }, []);

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleClose = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    // Offline: signup-complete is the call that creates the profile/terms —
    // dropping it strands the account, so block and keep the user on the step.
    if (OFFLINE_ENABLED && !isEffectivelyOnline()) {
      setCompleteError('blocked');
      return;
    }
    setCompleteError(null);
    setLoading(true);
    try {
      const services = data.servicesOffered.map((s) =>
        s === 'other' && data.otherService.trim() ? `other: ${data.otherService.trim()}` : s,
      );
      const referral =
        data.referralSource === 'other' && data.otherReferral.trim()
          ? `other: ${data.otherReferral.trim()}`
          : data.referralSource;
      const software =
        data.currentSoftware === 'Other' && data.otherSoftware.trim()
          ? `other: ${data.otherSoftware.trim()}`
          : data.currentSoftware;

      await api.post('/public/signup-complete', {
        // Blank goes over the wire blank. The backend only writes a non-empty
        // name, so an established clinic keeps its real one; a fresh signup
        // keeps the name /public/signup created. A placeholder here could rename
        // clinics whose owner reached this wizard with the mount-time prefill
        // failed (opened offline, submitted once signal returned).
        clinicName: data.clinicName.trim(),
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        name: userData.name || userData.email?.split('@')[0] || '',
        termsAccepted: true,
        termsVersion: '1.0',
        website: data.website || undefined,
        servicesOffered: services.length > 0 ? services : undefined,
        teamSize: data.teamSize || undefined,
        referralSource: referral || undefined,
        currentSoftware: software || undefined,
      });

      // The owner-redirect guard (App.tsx) reads the cached ['current-user-me']
      // with a 5-minute staleTime — without stamping the cache here,
      // navigate('/') bounces straight back into the wizard at step 1 until
      // the cache expires. Only the success path stamps: an unsubmitted setup
      // SHOULD be re-offered.
      queryClient.setQueryData(['current-user-me'], (old: any) =>
        old
          ? {
              ...old,
              clinic: { ...(old.clinic ?? {}), onboardingCompletedAt: new Date().toISOString() },
            }
          : old,
      );

      // Outside OfflineProvider nothing else reports reachability on this
      // route — feed our own round-trip back so a latched observed-down state
      // self-corrects.
      if (OFFLINE_ENABLED) reportConnectivitySuccess();
      localStorage.removeItem('onboardingUserData');
      navigate('/');
    } catch (err: any) {
      console.error('Onboarding complete error:', err);
      if (OFFLINE_ENABLED) reportFromError(err);
      // Transport failure (request never reached the server): the answers are
      // still in state — stay on the step and offer a retry instead of
      // silently dropping them and stranding the account.
      if (OFFLINE_ENABLED && isTransportError(err)) {
        setCompleteError('failed');
        return;
      }
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId: string) => {
    setData((prev) => {
      const current = prev.servicesOffered;
      if (current.includes(serviceId)) {
        return { ...prev, servicesOffered: current.filter((s) => s !== serviceId) };
      }
      if (current.length >= 3) return prev;
      return { ...prev, servicesOffered: [...current, serviceId] };
    });
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <PracticeNameStep data={data} setData={setData} />;
      case 1:
        return (
          <ServicesStep
            selected={data.servicesOffered}
            onToggle={toggleService}
            otherText={data.otherService}
            onOtherChange={(val) => setData({ ...data, otherService: val })}
          />
        );
      case 2:
        return (
          <TeamSizeStep
            selected={data.teamSize}
            onSelect={(val) => setData({ ...data, teamSize: val })}
          />
        );
      case 3:
        return (
          <ReferralStep
            selected={data.referralSource}
            onSelect={(val) => setData({ ...data, referralSource: val })}
            otherText={data.otherReferral}
            onOtherChange={(val) => setData({ ...data, otherReferral: val })}
          />
        );
      case 4:
        return (
          <SoftwareStep
            selected={data.currentSoftware}
            onSelect={(val) => setData({ ...data, currentSoftware: val })}
            otherText={data.otherSoftware}
            onOtherChange={(val) => setData({ ...data, otherSoftware: val })}
          />
        );
      default:
        return null;
    }
  };

  const canContinue = () => {
    if (step === 0 && !data.clinicName.trim()) return false;
    if (step === 1 && data.servicesOffered.includes('other') && !data.otherService.trim()) return false;
    if (step === 3 && data.referralSource === 'other' && !data.otherReferral.trim()) return false;
    if (step === 4 && data.currentSoftware === 'Other' && !data.otherSoftware.trim()) return false;
    return true;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
      }}
    >
      {/* Progress bar */}
      <Box sx={{ position: 'relative' }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            bgcolor: '#E5E7EB',
            '& .MuiLinearProgress-bar': { bgcolor: '#C9772B' },
          }}
        />
      </Box>

      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 1.5,
        }}
      >
        <IconButton onClick={handleBack} disabled={step === 0} size="small">
          <ArrowBack fontSize="small" />
        </IconButton>
        <IconButton onClick={handleClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 4,
        }}
      >
        <Box sx={{ maxWidth: 520, width: '100%' }}>
          {renderStep()}

          {completeError && (
            <Alert
              severity={completeError === 'blocked' ? 'info' : 'warning'}
              sx={{ mt: 3 }}
              action={
                completeError === 'failed' ? (
                  <Button color="inherit" size="small" onClick={handleComplete} disabled={loading}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {completeError === 'blocked'
                ? "You're offline — reconnect to finish account setup"
                : "We couldn't reach the server. Your answers are saved here — retry when you're back online."}
            </Alert>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || !canContinue()}
              sx={{
                bgcolor: '#C9772B',
                '&:hover': { bgcolor: '#6B4727' },
                '&.Mui-disabled': { bgcolor: '#D1C4B2', color: '#fff' },
                py: 1.2,
                px: 4,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              {loading ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/* ============================================================
   Step Components
   ============================================================ */

function PracticeNameStep({
  data,
  setData,
}: {
  data: OnboardingData;
  setData: React.Dispatch<React.SetStateAction<OnboardingData>>;
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: '#C9772B', fontWeight: 600, letterSpacing: 1.5 }}
      >
        ACCOUNT SETUP
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
        What's your practice name?
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        This is the brand name your clients will see. Your billing and legal name can be added later.
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Practice name
      </Typography>
      <TextField
        fullWidth
        placeholder="e.g. Oakridge Equine Clinic"
        value={data.clinicName}
        onChange={(e) => setData({ ...data, clinicName: e.target.value })}
        sx={{ mb: 3 }}
        size="medium"
      />

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Website{' '}
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          (Optional)
        </Typography>
      </Typography>
      <TextField
        fullWidth
        placeholder="www.yourpractice.com"
        value={data.website}
        onChange={(e) => setData({ ...data, website: e.target.value })}
        size="medium"
      />
    </>
  );
}

function ServicesStep({
  selected,
  onToggle,
  otherText,
  onOtherChange,
}: {
  selected: string[];
  onToggle: (id: string) => void;
  otherText: string;
  onOtherChange: (val: string) => void;
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: '#C9772B', fontWeight: 600, letterSpacing: 1.5 }}
      >
        ACCOUNT SETUP
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
        What services do you offer?
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Choose your primary and up to 3 related service types
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
        }}
      >
        {SERVICE_OPTIONS.map((service) => {
          const isSelected = selected.includes(service.id);
          const Icon = service.icon;
          return (
            <Paper
              key={service.id}
              elevation={0}
              onClick={() => onToggle(service.id)}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isSelected ? '#C9772B' : '#E5E7EB',
                borderRadius: 2,
                bgcolor: isSelected ? 'rgba(139,94,52,0.04)' : '#fff',
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: isSelected ? '#C9772B' : '#9CA3AF',
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 24,
                  color: isSelected ? '#C9772B' : '#6B7280',
                  mb: 1,
                  display: 'block',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#C9772B' : 'text.primary',
                }}
              >
                {service.label}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {selected.includes('other') && (
        <TextField
          fullWidth
          placeholder="Please specify the service"
          value={otherText}
          onChange={(e) => onOtherChange(e.target.value)}
          size="small"
          sx={{ mt: 2 }}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </>
  );
}

function TeamSizeStep({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (val: string) => void;
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: '#C9772B', fontWeight: 600, letterSpacing: 1.5 }}
      >
        ACCOUNT SETUP
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
        What's your team size?
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        This will help us set up your calendar correctly. Don't worry, this doesn't change the price
        - you can have unlimited team members for free on StableTrack!
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {TEAM_SIZE_OPTIONS.map((option) => (
          <Paper
            key={option.value}
            elevation={0}
            onClick={() => onSelect(option.value)}
            sx={{
              px: 3,
              py: 2,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: selected === option.value ? '#C9772B' : '#E5E7EB',
              borderRadius: 2,
              bgcolor: selected === option.value ? 'rgba(139,94,52,0.04)' : '#fff',
              transition: 'all 0.15s',
              '&:hover': {
                borderColor: selected === option.value ? '#C9772B' : '#9CA3AF',
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: selected === option.value ? 600 : 500,
                color: selected === option.value ? '#C9772B' : 'text.primary',
              }}
            >
              {option.label}
            </Typography>
          </Paper>
        ))}
      </Box>

    </>
  );
}

function ReferralStep({
  selected,
  onSelect,
  otherText,
  onOtherChange,
}: {
  selected: string;
  onSelect: (val: string) => void;
  otherText: string;
  onOtherChange: (val: string) => void;
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: '#C9772B', fontWeight: 600, letterSpacing: 1.5 }}
      >
        ACCOUNT SETUP
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
        How did you hear about StableTrack?
      </Typography>

      <RadioGroup
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
        sx={{ mt: 2 }}
      >
        {REFERRAL_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio
                sx={{
                  color: '#9CA3AF',
                  '&.Mui-checked': { color: '#C9772B' },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
            }
            sx={{ mb: 0.5 }}
          />
        ))}
      </RadioGroup>

      {selected === 'other' && (
        <TextField
          fullWidth
          placeholder="Please tell us how you heard about us"
          value={otherText}
          onChange={(e) => onOtherChange(e.target.value)}
          size="small"
          sx={{ mt: 1, ml: 4 }}
        />
      )}
    </>
  );
}

function SoftwareStep({
  selected,
  onSelect,
  otherText,
  onOtherChange,
}: {
  selected: string;
  onSelect: (val: string) => void;
  otherText: string;
  onOtherChange: (val: string) => void;
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: '#C9772B', fontWeight: 600, letterSpacing: 1.5 }}
      >
        ACCOUNT SETUP
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
        Which software are you currently using?
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        If you're looking to switch, we can help speed up your business setup and import your data
        into your new StableTrack account.
      </Typography>

      <Box
        sx={{
          maxHeight: 360,
          overflowY: 'auto',
          pr: 1,
        }}
      >
        <RadioGroup
          value={selected}
          onChange={(e) => onSelect(e.target.value)}
        >
          {SOFTWARE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={
                <Radio
                  sx={{
                    color: '#9CA3AF',
                    '&.Mui-checked': { color: '#C9772B' },
                  }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {option}
                </Typography>
              }
              sx={{ mb: 0.5 }}
            />
          ))}
        </RadioGroup>

        {selected === 'Other' && (
          <TextField
            fullWidth
            placeholder="Please specify the software"
            value={otherText}
            onChange={(e) => onOtherChange(e.target.value)}
            size="small"
            sx={{ mt: 1, ml: 4 }}
          />
        )}
      </Box>
    </>
  );
}

export default Onboarding;
